import { Component } from '@theme/component';
import { createDebugger } from '@theme/debug';

const debug = createDebugger('card-gallery-variant-filter', false);

/**
 * Filters card gallery media based on the variant-id from the parent list item
 *
 * @extends {Component}
 */
export class CardGalleryVariantFilter extends Component {
    connectedCallback() {
        super.connectedCallback();
        this.filterMediaForVariant();
    }

    filterMediaForVariant() {
        // Find parent list item with variant-id
        const listItem = this.closest('li[data-variant-id]');
        const variantId = listItem?.getAttribute('data-variant-id');

        if (!variantId) {
            debug.log('No variant-id found on parent li, skipping filtering');
            return;
        }

        const productId = this.getAttribute('data-product-id');
        const variantMediaMap = this.getAttribute('data-variant-media-map');
        const delimitersStr = this.getAttribute('data-variant-media-delimiters');
        const firstVariantPositionAttr = this.getAttribute('data-first-variant-position');
        const firstVariantPosition = firstVariantPositionAttr ? parseInt(firstVariantPositionAttr) : null;

        debug.log('Starting variant media filtering', {
            variantId,
            productId,
            variantMediaMap,
            delimitersStr,
            firstVariantPosition
        });

        if (!variantMediaMap || !delimitersStr) {
            debug.log('Missing variant media data, skipping filtering');
            return;
        }

        // Parse variant media map (variant_id:position,...)
        /** @type {Record<string, number>} */
        const variantPositions = {};
        variantMediaMap.split(',').forEach((pair) => {
            if (pair) {
                const [id, pos] = pair.split(':');
                if (id && pos) {
                    variantPositions[id] = parseInt(pos);
                }
            }
        });

        // Parse delimiters
        const delimiters = delimitersStr
            .split(',')
            .map((n) => parseInt(n))
            .filter((n) => !isNaN(n));

        // Get current variant's position
        const currentVariantPosition = variantPositions[variantId];

        if (!currentVariantPosition) {
            debug.log('No media position found for variant, showing all media');
            return;
        }

        // Find next delimiter after current position
        let nextPosition = null;
        for (const delimiter of delimiters) {
            if (delimiter > currentVariantPosition) {
                nextPosition = delimiter;
                break;
            }
        }

        // If no next delimiter, show until end
        if (nextPosition === null) {
            nextPosition = Infinity;
        }

        debug.log('Filtering media', {
            currentVariantPosition,
            nextPosition,
            delimiters
        });

        // Filter slideshow slides
        const slideshow = this.querySelector('[ref="slideshow"]');
        if (!slideshow) {
            debug.log('No slideshow found');
            return;
        }

        const slides = slideshow.querySelectorAll('[slide-id]');
        let visibleCount = 0;

        slides.forEach((slide, index) => {
            const htmlSlide = /** @type {HTMLElement} */ (slide);
            const slideId = htmlSlide.getAttribute('slide-id');
            // Find the media position for this slide
            const mediaPosition = this.#getMediaPosition(htmlSlide);

            const shouldShow = mediaPosition >= currentVariantPosition && mediaPosition < nextPosition;

            debug.log(`Slide ${index}`, {
                slideId,
                mediaPosition,
                currentVariantPosition,
                nextPosition,
                shouldShow
            });

            if (shouldShow) {
                htmlSlide.hidden = false;
                htmlSlide.style.display = '';
                visibleCount++;
            } else {
                htmlSlide.hidden = true;
                htmlSlide.style.display = 'none';
            }
        });

        debug.log(`Filtered: ${visibleCount} slides visible out of ${slides.length}`);

        // Hide arrows if there's only 1 visible slide
        const arrowsComponent = /** @type {HTMLElement | null} */ (
            slideshow.querySelector('slideshow-arrows')
        );
        if (arrowsComponent) {
            if (visibleCount <= 1) {
                arrowsComponent.style.display = 'none';
                debug.log('Hiding arrows - only 1 visible slide');
            } else {
                arrowsComponent.style.display = '';
                debug.log('Showing arrows - multiple visible slides');
            }
        }

        // Reset slideshow to first visible slide
        setTimeout(() => {
            const slideshowComponent = /** @type {any} */ (slideshow);
            if (slideshowComponent.select) {
                slideshowComponent.select(0);
            }
        }, 0);
    }

    /**
     * Get the media position for a slide
     * @param {HTMLElement} slide
     * @returns {number}
     */
    #getMediaPosition(slide) {
        // Try to find position from data attribute
        const positionAttr = slide?.getAttribute('data-media-position');

        if (positionAttr) {
            const pos = parseInt(positionAttr);
            debug.log(`Found media position from attribute: ${pos}`);
            return pos;
        }

        // Fallback: use slide index + 1
        const slides = this.querySelectorAll('[slide-id]');
        const slideArray = Array.from(slides);
        const index = slideArray.indexOf(slide);
        const fallbackPos = index >= 0 ? index + 1 : 1;
        debug.log(`Using fallback position: ${fallbackPos}`);
        return fallbackPos;
    }
}

if (!customElements.get('card-gallery-variant-filter')) {
    customElements.define('card-gallery-variant-filter', CardGalleryVariantFilter);
}
